import { useEffect, useState } from "react"
import { ErroreApi, api } from "../api/client"
import type { InfoInvito, InfoReset, Sessione } from "../api/tipi"
import "./ingresso.css"

const EMOJI = ["🎤", "🍕", "🌶️", "🦖", "🪩", "🌻", "👑", "🛸", "🐍", "🍸"]

/** I messaggi del server sono già in italiano e pensati per chi li legge.
 *  La validazione di FastAPI no, ma i campi la anticipano (minLength). */
function spiega(e: unknown): string {
  if (e instanceof ErroreApi) {
    if (e.codice === 0) return "Non c'è connessione. Riprova quando torna la rete."
    if (e.codice === 422) return "Controlla i campi: la password vuole almeno 6 caratteri."
    return e.message
  }
  return "Qualcosa è andato storto. Riprova."
}

function Testata({ occhiello }: { occhiello: string }) {
  return (
    <header className="ingresso__testata">
      <p className="occhiello">{occhiello}</p>
      <h1 className="ingresso__titolo">Giuria Trash</h1>
    </header>
  )
}

function Attesa({ testo }: { testo: string }) {
  return (
    <div className="schermo ingresso">
      <p className="ingresso__nota">{testo}</p>
    </div>
  )
}

/** Link scaduto, rigenerato o di un'edizione chiusa: si dice perché e si
 *  offre l'uscita verso l'accesso normale. */
function LinkRotto({ messaggio }: { messaggio: string }) {
  return (
    <div className="schermo ingresso">
      <Testata occhiello="La giuria più severa d'Italia" />
      <p className="avviso">{messaggio}</p>
      <a className="bottone primario ingresso__entra ingresso__link" href="/">
        Vai all'accesso
      </a>
    </div>
  )
}

function CampoNickname({ valore, onCambia }: { valore: string; onCambia: (v: string) => void }) {
  return (
    <>
      <label className="ingresso__etichetta" htmlFor="nickname">
        Nickname
      </label>
      <input
        id="nickname"
        className="campo"
        value={valore}
        onChange={(e) => onCambia(e.target.value)}
        autoComplete="username"
        spellCheck={false}
        maxLength={40}
        placeholder="Luca"
        required
      />
    </>
  )
}

function CampoPassword({
  valore,
  onCambia,
  nuova,
}: {
  valore: string
  onCambia: (v: string) => void
  nuova: boolean
}) {
  const [visibile, setVisibile] = useState(false)
  return (
    <>
      <label className="ingresso__etichetta" htmlFor="password">
        {nuova ? "Scegli una password" : "Password"}
      </label>
      <div className="ingresso__password">
        <input
          id="password"
          className="campo"
          type={visibile ? "text" : "password"}
          value={valore}
          onChange={(e) => onCambia(e.target.value)}
          autoComplete={nuova ? "new-password" : "current-password"}
          minLength={nuova ? 6 : undefined}
          maxLength={128}
          required
        />
        <button
          type="button"
          className="ingresso__occhio"
          aria-controls="password"
          onClick={() => setVisibile((v) => !v)}
        >
          {visibile ? "Nascondi" : "Mostra"}
        </button>
      </div>
      {nuova && <p className="ingresso__aiuto">Almeno 6 caratteri, nessun'altra regola.</p>}
    </>
  )
}

function SceltaEmoji({ scelta, onScegli }: { scelta: string; onScegli: (e: string) => void }) {
  return (
    <fieldset className="ingresso__emoji">
      <legend className="ingresso__etichetta">Il tuo simbolo</legend>
      {EMOJI.map((e) => (
        <button
          key={e}
          type="button"
          className="ingresso__pallino"
          aria-label={`Scegli ${e}`}
          aria-pressed={e === scelta}
          onClick={() => onScegli(e)}
        >
          {e}
        </button>
      ))}
    </fieldset>
  )
}

/** Chi apre il sito senza essere collegato: un altro telefono, l'app
 *  installata (che su iPhone non vede la sessione di Safari), o dopo "Esci". */
export function Accedi({
  avviso,
  onEntrato,
}: {
  avviso: string | null
  onEntrato: (s: Sessione) => void
}) {
  const [nickname, setNickname] = useState("")
  const [password, setPassword] = useState("")
  const [errore, setErrore] = useState<string | null>(avviso)
  const [inCorso, setInCorso] = useState(false)

  async function invia(ev: React.FormEvent) {
    ev.preventDefault()
    setErrore(null)
    setInCorso(true)
    try {
      onEntrato(await api.accedi({ nickname: nickname.trim(), password }))
    } catch (e) {
      setErrore(spiega(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form className="schermo ingresso" onSubmit={invia}>
      <Testata occhiello="La giuria più severa d'Italia" />
      <div className="scheda ingresso__campi">
        <CampoNickname valore={nickname} onCambia={setNickname} />
        <CampoPassword valore={password} onCambia={setPassword} nuova={false} />
      </div>
      {errore && <p className="avviso">{errore}</p>}
      <button className="bottone primario ingresso__entra" type="submit" disabled={inCorso}>
        {inCorso ? "Un attimo…" : "Entra"}
      </button>
      <p className="ingresso__nota">
        Prima volta? Ti serve il link d'invito: chiedilo a chi fa la regia.
      </p>
    </form>
  )
}

/** Il link d'invito dell'edizione: ci si crea l'account, o si entra nel
 *  gruppo con quello che si ha già (l'anno dopo, o da un altro telefono). */
export function Invito({ codice, onEntrato }: { codice: string; onEntrato: (s: Sessione) => void }) {
  const [edizione, setEdizione] = useState<InfoInvito["edition"] | null>(null)
  const [erroreLink, setErroreLink] = useState<string | null>(null)
  const [nuovo, setNuovo] = useState(true)
  const [nickname, setNickname] = useState("")
  const [password, setPassword] = useState("")
  const [emoji, setEmoji] = useState(EMOJI[0])
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  useEffect(() => {
    api.invito(codice).then(
      (r) => setEdizione(r.edition),
      (e) => setErroreLink(spiega(e)),
    )
  }, [codice])

  async function invia(ev: React.FormEvent) {
    ev.preventDefault()
    setErrore(null)
    setInCorso(true)
    try {
      onEntrato(
        nuovo
          ? await api.registrati({ join_code: codice, nickname: nickname.trim(), password, emoji })
          : await api.accedi({ nickname: nickname.trim(), password, join_code: codice }),
      )
    } catch (e) {
      // Il nickname c'è già: quasi sempre è la stessa persona su un altro
      // telefono, o l'anno dopo. Si passa all'accesso tenendo il nome.
      if (nuovo && e instanceof ErroreApi && e.codice === 409) {
        setNuovo(false)
        setPassword("")
      }
      setErrore(spiega(e))
    } finally {
      setInCorso(false)
    }
  }

  if (erroreLink) return <LinkRotto messaggio={erroreLink} />
  if (!edizione) return <Attesa testo="Apro l'invito…" />
  if (edizione.status === "closed") {
    return <LinkRotto messaggio={`${edizione.name} è chiusa: questo link non vale più.`} />
  }

  return (
    <form className="schermo ingresso" onSubmit={invia}>
      <Testata occhiello={`Invito · ${edizione.name}`} />

      <div className="ingresso__modi" role="group" aria-label="Hai già un account?">
        <button
          type="button"
          className="ingresso__modo"
          aria-pressed={nuovo}
          onClick={() => {
            setNuovo(true)
            setErrore(null)
          }}
        >
          Prima volta
        </button>
        <button
          type="button"
          className="ingresso__modo"
          aria-pressed={!nuovo}
          onClick={() => {
            setNuovo(false)
            setErrore(null)
          }}
        >
          Ho già un account
        </button>
      </div>

      <div className="scheda ingresso__campi">
        <CampoNickname valore={nickname} onCambia={setNickname} />
        <CampoPassword
          key={nuovo ? "nuova" : "attuale"}
          valore={password}
          onCambia={setPassword}
          nuova={nuovo}
        />
        {nuovo && <SceltaEmoji scelta={emoji} onScegli={setEmoji} />}
      </div>

      {errore && <p className="avviso">{errore}</p>}

      <button className="bottone primario ingresso__entra" type="submit" disabled={inCorso}>
        {inCorso ? "Un attimo…" : nuovo ? "Crea l'account ed entra" : "Entra nel gruppo"}
      </button>

      <p className="ingresso__nota">
        {nuovo
          ? "Nickname e password valgono anche per le prossime edizioni."
          : "Entri in questo gruppo con il tuo nome e la tua emoji."}
      </p>
    </form>
  )
}

/** Il link di reset mandato dalla regia: si sceglie la password nuova e si
 *  entra. Gli altri telefoni con lo stesso account si scollegano. */
export function NuovaPassword({
  codice,
  onEntrato,
  onFatto,
}: {
  codice: string
  onEntrato: (s: Sessione) => void
  onFatto: () => void
}) {
  const [chi, setChi] = useState<InfoReset | null>(null)
  const [erroreLink, setErroreLink] = useState<string | null>(null)
  const [password, setPassword] = useState("")
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [cambiata, setCambiata] = useState(false)

  useEffect(() => {
    api.infoReset(codice).then(setChi, (e) => setErroreLink(spiega(e)))
  }, [codice])

  async function invia(ev: React.FormEvent) {
    ev.preventDefault()
    setErrore(null)
    setInCorso(true)
    try {
      const { sessione } = await api.reset(codice, password)
      if (sessione) onEntrato(sessione)
      else setCambiata(true)
    } catch (e) {
      setErrore(spiega(e))
    } finally {
      setInCorso(false)
    }
  }

  if (erroreLink) return <LinkRotto messaggio={erroreLink} />
  if (!chi) return <Attesa testo="Apro il link…" />

  if (cambiata) {
    return (
      <div className="schermo ingresso">
        <Testata occhiello="Password cambiata" />
        <p className="scheda ingresso__messaggio">
          Fatto, {chi.nickname}. Adesso non sei in nessuna giuria in corso: quando arriva il link
          della prossima edizione, entra con la nuova password.
        </p>
        <button type="button" className="bottone primario ingresso__entra" onClick={onFatto}>
          Vai all'accesso
        </button>
      </div>
    )
  }

  return (
    <form className="schermo ingresso" onSubmit={invia}>
      <Testata occhiello={`Ciao ${chi.nickname} ${chi.emoji}`} />
      {/* Serve al portachiavi del telefono: senza un campo col nome salva la
          password nuova senza sapere di chi è. */}
      <input
        className="ingresso__nascosto"
        type="text"
        name="username"
        autoComplete="username"
        value={chi.nickname}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      <div className="scheda ingresso__campi">
        <CampoPassword valore={password} onCambia={setPassword} nuova />
      </div>
      {errore && <p className="avviso">{errore}</p>}
      <button className="bottone primario ingresso__entra" type="submit" disabled={inCorso}>
        {inCorso ? "Un attimo…" : "Salva ed entra"}
      </button>
      <p className="ingresso__nota">
        Il link vale una volta sola. Gli altri telefoni con il tuo account si scollegano.
      </p>
    </form>
  )
}
